import React, { Fragment, useState } from 'react';
import { SearchInput } from '@mds/ui';
import {
    FormControl,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import {
    ArticleSelectionKind,
    articleSelectionLabel,
} from '../../../shared/global-enums';
import { CATEGORIES_TREE } from '../../../gql/categories';
import { useQuery } from '@apollo/react-hooks';
import { ToolbarMenuItem, CategoryItemNode } from '../../../shared/types';
import { categoriesToList } from '../../../utils/list-utils';
import { topicsOrCategories } from '../../../utils/utils';

export interface ArticleFilterProps {
    searchString: string;
    articleKind: ArticleSelectionKind;
    selectedCategory?: string;
    onQueryStringChange: (value: string) => void;
    onSelectedKindChange: (value: ArticleSelectionKind) => void;
    onSelectedCategoryChange: (value: string) => void;
}

const ArticleFilter = (props: ArticleFilterProps) => {
    const [inputValue, setInputValue] = useState(props.searchString);
    const { loading, data } = useQuery(CATEGORIES_TREE, {
        fetchPolicy: 'network-only',
    });

    if (loading) return <></>;
    const treeData = (data?.categoriesAsTree as CategoryItemNode[]) ?? [];

    const categoryList = [
        { title: `All ${topicsOrCategories(true)}`, value: '' },
        ...categoriesToList<ToolbarMenuItem>(treeData, (cat) => {
            return {
                title: cat.name,
                value: cat._id,
                indentation: cat.level,
            };
        }),
    ];

    return (
        <Fragment>
            <Stack
                id="article-toolbar"
                direction={{ xs: 'column', lg: 'row' }}
                spacing={1.5}
                sx={{
                    alignItems: { xs: 'stretch', lg: 'center' },
                    justifyContent: 'space-between',
                }}
            >
                <ToggleButtonGroup
                    exclusive
                    size="medium"
                    value={props.articleKind}
                    aria-label="Article status filter"
                    onChange={(_, nextValue: ArticleSelectionKind | null) => {
                        if (nextValue) {
                            props.onSelectedKindChange(nextValue);
                        }
                    }}
                    sx={{ flexWrap: 'wrap' }}
                >
                    {Object.values(ArticleSelectionKind).map((item) => (
                        <ToggleButton key={item} value={item}>
                            {
                                articleSelectionLabel[
                                    item as ArticleSelectionKind
                                ]
                            }
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    sx={{
                        alignItems: { xs: 'stretch', sm: 'center' },
                    }}
                >
                    <FormControl
                        hiddenLabel
                        sx={{ width: { xs: '100%', sm: 180 } }}
                    >
                        <Select
                            id="article-toolbar-category-filter"
                            inputProps={{
                                'aria-label': `Filter by ${topicsOrCategories(true)}`,
                            }}
                            displayEmpty
                            value={props.selectedCategory ?? ''}
                            onChange={(event: SelectChangeEvent<string>) =>
                                props.onSelectedCategoryChange(
                                    event.target.value
                                )
                            }
                            sx={{ '& .MuiSelect-select': { pr: 5 } }}
                        >
                            {categoryList.map((item) => (
                                <MenuItem
                                    key={item.value}
                                    value={item.value}
                                    sx={{ pl: 2 + (item.indentation ?? 0) * 2 }}
                                >
                                    {item.title}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <SearchInput
                        id="article-toolbar-search"
                        placeholder="Search articles..."
                        value={inputValue}
                        onChange={(
                            event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                            setInputValue(event.target.value);
                            props.onQueryStringChange(event.target.value);
                        }}
                        sx={{ width: { xs: '100%', sm: 320 } }}
                    />
                </Stack>
            </Stack>
        </Fragment>
    );
};

export default ArticleFilter;
